import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ChevronLeft, ChevronRight, X, Users } from 'lucide-react'
import { getOrders, updateOrder } from '@/api/orders'
import type { Order } from '@/api/orders'
import { driverApi } from '@/api/drivers'
import { leaveApi } from '@/api/leaves'
import type { Driver } from '@/types'
import { KanbanColumn, OrderCard, type KanbanCardData } from '@/components/dispatchBoard/KanbanColumn'

const UNASSIGNED = ''

const CATEGORY_BADGE_CLASS: Record<string, string> = {
  '送機': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  '接機': 'bg-orange-500 dark:bg-orange-600 text-white',
  '其他': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300',
}

// 卡片本身的底色，跟派車單列表的接機/送機配色一致，方便一眼辨識類別
const CATEGORY_CARD_BG_CLASS: Record<string, string> = {
  '送機': 'bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800',
  '接機': 'bg-orange-50 border-orange-300 dark:bg-orange-950/30 dark:border-orange-700',
  '其他': 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700',
}

function toCardData(order: Order): KanbanCardData {
  return {
    id: order.id,
    time: order.pickup_datetime ? format(new Date(order.pickup_datetime), 'HH:mm') : '--:--',
    category: order.order_category,
    pickupLocation: order.pickup_location,
    dropoffLocation: order.dropoff_location,
    amount: order.pricing?.total_amount || 0,
  }
}

export default function DispatchBoardPage() {
  const navigate = useNavigate()
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [orders, setOrders] = useState<Order[]>([])
  const [drivers, setDrivers] = useState<Driver[]>([])
  const [onLeaveDriverIds, setOnLeaveDriverIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [draggingOrderId, setDraggingOrderId] = useState<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  // 一進入頁面（或切換日期）就自動只顯示「當天有指派訂單」的司機，其他自動隱藏——
  // 每次成功抓取當天資料後在 fetchData 裡重新計算，不記憶跨日的手動設定。
  const [hiddenDriverIds, setHiddenDriverIds] = useState<Set<string>>(new Set())
  const [showDriverFilter, setShowDriverFilter] = useState(true)

  const toggleDriverVisible = (driverId: string) => {
    setHiddenDriverIds(prev => {
      const next = new Set(prev)
      if (next.has(driverId)) next.delete(driverId)
      else next.add(driverId)
      return next
    })
  }

  const showAllDrivers = () => {
    setHiddenDriverIds(new Set())
  }

  const hideAllDrivers = async () => {
    const affectedOrders = orders.filter(o => o.driver_id && drivers.some(d => d.id === o.driver_id))
    if (affectedOrders.length > 0) {
      const ok = window.confirm(
        `全部隱藏後，這 ${affectedOrders.length} 筆已指派司機的訂單會一併清空司機、改為「未指派」（會寫回資料庫，不只是畫面顯示）。確定要繼續嗎？`
      )
      if (!ok) return
      const prevOrders = orders
      setOrders(prev => prev.map(o => affectedOrders.some(a => a.id === o.id) ? { ...o, driver_id: undefined } : o))
      setIsSaving(true)
      try {
        await Promise.all(affectedOrders.map(o => updateOrder(o.id, { driver_id: null, driver_name: null, driver_phone: null, vehicle_number: null } as any)))
      } catch (error) {
        console.error('Failed to unassign drivers:', error)
        setOrders(prevOrders)
        alert('清空司機失敗，請稍後再試')
        setIsSaving(false)
        return
      }
      setIsSaving(false)
    }

    setHiddenDriverIds(new Set(drivers.map(d => d.id)))
  }

  const visibleDrivers = useMemo(() => drivers.filter(d => !hiddenDriverIds.has(d.id)), [drivers, hiddenDriverIds])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [ordersRes, driversRes, leaveIds] = await Promise.all([
        getOrders({ date_from: date, date_to: date, limit: 500 }),
        driverApi.getDrivers(1, 100),
        leaveApi.getOnDateDriverIds(date).catch(() => [] as string[]),
      ])
      const validOrders = (ordersRes.data || []).filter(o => o.status !== '已取消')
      const driverList = driversRes.items || []
      setOrders(validOrders)
      setDrivers(driverList)
      setOnLeaveDriverIds(leaveIds)

      // 自動只顯示當天有指派訂單的司機，其他司機自動隱藏
      const driversWithOrders = new Set(validOrders.filter(o => o.driver_id).map(o => o.driver_id as string))
      setHiddenDriverIds(new Set(driverList.filter(d => !driversWithOrders.has(d.id)).map(d => d.id)))
    } catch (error) {
      console.error('Failed to fetch dispatch board data:', error)
    } finally {
      setLoading(false)
    }
  }, [date])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const columns = useMemo(() => {
    const buckets = new Map<string, Order[]>()
    buckets.set(UNASSIGNED, [])
    for (const driver of drivers) buckets.set(driver.id, [])
    for (const order of orders) {
      const key = order.driver_id && buckets.has(order.driver_id) ? order.driver_id : UNASSIGNED
      buckets.get(key)!.push(order)
    }
    for (const list of buckets.values()) {
      list.sort((a, b) => (a.pickup_datetime || '').localeCompare(b.pickup_datetime || ''))
    }
    return buckets
  }, [orders, drivers])

  const carriedOrder = orders.find(o => o.id === draggingOrderId) ?? null

  const handleDrop = useCallback(async (driverId: string) => {
    setDragOverKey(null)
    if (!draggingOrderId || isSaving) return
    const orderId = draggingOrderId
    const targetDriverId = driverId === UNASSIGNED ? undefined : driverId
    setDraggingOrderId(null)

    // 樂觀更新：先在畫面上把卡片移過去，失敗再復原
    const prevOrders = orders
    const targetDriver = targetDriverId ? drivers.find(d => d.id === targetDriverId) : undefined
    const assignment = {
      driver_id: targetDriverId ?? null,
      driver_name: targetDriver?.name ?? null,
      driver_phone: targetDriver?.phone ?? null,
      vehicle_number: targetDriver?.vehicle_plate ?? null,
    }
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      driver_id: targetDriverId,
      driver_name: assignment.driver_name ?? undefined,
      driver_phone: assignment.driver_phone ?? undefined,
      vehicle_number: assignment.vehicle_number ?? undefined,
      status: targetDriverId ? (o.status === '待派車' ? '已派車' : o.status) : (o.status === '已派車' ? '待派車' : o.status),
    } : o))
    setIsSaving(true)
    try {
      // 取消指派要明確送 null，undefined 會被 JSON 丟掉、後端就不會清掉司機
      await updateOrder(orderId, assignment as any)
    } catch (error) {
      console.error('Failed to assign driver:', error)
      setOrders(prevOrders)
      alert('指派失敗，請稍後再試')
    } finally {
      setIsSaving(false)
    }
  }, [draggingOrderId, isSaving, orders])

  const goToDay = (delta: number) => {
    const d = new Date(date)
    d.setDate(d.getDate() + delta)
    setDate(format(d, 'yyyy-MM-dd'))
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/50">
        <div className="mx-auto max-w-7xl px-3 py-6 sm:px-4 md:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">調度管理</h1>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">拖曳訂單卡片到司機欄位即可指派</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goToDay(-1)}
                className="rounded-lg border border-gray-300 dark:border-gray-600 p-2 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 text-sm outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => goToDay(1)}
                className="rounded-lg border border-gray-300 dark:border-gray-600 p-2 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setShowDriverFilter(v => !v)}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                  hiddenDriverIds.size > 0
                    ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <Users className="h-4 w-4" />
                顯示司機
                {hiddenDriverIds.size > 0 && (
                  <span className="rounded-full bg-blue-600 px-1.5 text-xs text-white">{drivers.length - hiddenDriverIds.size}</span>
                )}
              </button>
            </div>
          </div>

          {showDriverFilter && (
            <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">選擇要顯示的司機欄位</span>
                <div className="flex gap-2 text-xs">
                  <button type="button" onClick={showAllDrivers} className="text-blue-600 dark:text-blue-400 hover:underline">全選</button>
                  <button type="button" onClick={hideAllDrivers} className="text-gray-500 dark:text-gray-400 hover:underline">全部隱藏</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {drivers.map(driver => {
                  const isVisible = !hiddenDriverIds.has(driver.id)
                  return (
                    <button
                      key={driver.id}
                      type="button"
                      onClick={() => toggleDriverVisible(driver.id)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                        isVisible
                          ? 'border-blue-300 bg-blue-100 text-blue-700 dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                          : 'border-gray-300 bg-white text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500'
                      }`}
                    >
                      {driver.name}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-full px-3 py-6 sm:px-4 md:px-6 lg:px-8">
        {carriedOrder && (
          <div className="mb-4 flex items-center justify-between gap-2 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-2 text-sm text-indigo-700 dark:text-indigo-300 md:hidden">
            <span>
              已拿起：{carriedOrder.pickup_datetime ? format(new Date(carriedOrder.pickup_datetime), 'HH:mm') : ''}{' '}
              {carriedOrder.pickup_location} → {carriedOrder.dropoff_location}　點司機欄位放下
            </span>
            <button
              type="button"
              onClick={() => setDraggingOrderId(null)}
              className="shrink-0 rounded-md bg-white dark:bg-gray-800 px-2 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 shadow-sm flex items-center gap-1"
            >
              <X className="h-3 w-3" /> 取消
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">載入中...</div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-4">
            <KanbanColumn
              title="未指派"
              subtitle={`${columns.get(UNASSIGNED)?.length || 0} 筆`}
              isEmpty={(columns.get(UNASSIGNED)?.length || 0) === 0}
              isDragOver={dragOverKey === UNASSIGNED}
              isCarrying={draggingOrderId !== null}
              onDragOver={() => setDragOverKey(UNASSIGNED)}
              onDragLeave={() => setDragOverKey(null)}
              onDrop={() => handleDrop(UNASSIGNED)}
            >
              {(columns.get(UNASSIGNED) || []).map(order => (
                <OrderCard
                  key={order.id}
                  card={toCardData(order)}
                  categoryBadgeClass={CATEGORY_BADGE_CLASS[order.order_category] || CATEGORY_BADGE_CLASS['其他']}
                  cardBgClass={CATEGORY_CARD_BG_CLASS[order.order_category] || CATEGORY_CARD_BG_CLASS['其他']}
                  isDragging={draggingOrderId === order.id}
                  anyCarried={draggingOrderId !== null}
                  onDragStart={() => setDraggingOrderId(order.id)}
                  onDragEnd={() => setDraggingOrderId(null)}
                  onClick={() => navigate(`/orders?editOrder=${order.id}`)}
                />
              ))}
            </KanbanColumn>

            {visibleDrivers.map(driver => {
              const isOnLeave = onLeaveDriverIds.includes(driver.id)
              const driverOrders = columns.get(driver.id) || []
              return (
                <KanbanColumn
                  key={driver.id}
                  title={`${isOnLeave ? '🏖️ ' : ''}${driver.name}`}
                  subtitle={`${driver.vehicle_plate} · ${driverOrders.length} 筆`}
                  isEmpty={driverOrders.length === 0}
                  isDragOver={dragOverKey === driver.id}
                  isCarrying={draggingOrderId !== null}
                  onDragOver={() => setDragOverKey(driver.id)}
                  onDragLeave={() => setDragOverKey(null)}
                  onDrop={() => handleDrop(driver.id)}
                >
                  {driverOrders.map(order => (
                    <OrderCard
                      key={order.id}
                      card={toCardData(order)}
                      categoryBadgeClass={CATEGORY_BADGE_CLASS[order.order_category] || CATEGORY_BADGE_CLASS['其他']}
                  cardBgClass={CATEGORY_CARD_BG_CLASS[order.order_category] || CATEGORY_CARD_BG_CLASS['其他']}
                      isDragging={draggingOrderId === order.id}
                      anyCarried={draggingOrderId !== null}
                      onDragStart={() => setDraggingOrderId(order.id)}
                      onDragEnd={() => setDraggingOrderId(null)}
                      onClick={() => navigate(`/orders?editOrder=${order.id}`)}
                    />
                  ))}
                </KanbanColumn>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
